import { useState, useContext, useEffect } from "react";

import {
  alpha,
  Box,
  List,
  styled,
  Button,
  ListItem,
  Collapse,
} from "@mui/material";
import { NavLink as RouterLink, useLocation } from "react-router-dom";
import { SidebarContext } from "../../../../contexts/SidebarContext";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import { useTranslation } from "react-i18next";
import moment from 'moment';
import { USER_INFO } from "@states";
import { useRecoilValue } from "recoil";
import HotelApi from "@services/hotel.api";
import {
  magnifying_glass,
  magnifying_glass_white,
//   map_pin,
//   map_pin_fill,
  pencil_line,
  pencil_line_white
} from "@assets";
import datyImg from "@assets/daty.svg";
import datyActiveImg from "@assets/daty_active.svg";
import datamenityImg from "@assets/datamenity.svg";
import datamenityActiveImg from "@assets/datamenity_active.svg";
import easyOrderImg from "@assets/easy_order.svg";
import easyOrderActiveImg from "@assets/easy_order_active.svg";
import arrowSquareOutImg from "@assets/arrow-square-out.png";
import arrowSquareOutActiveImg from "@assets/arrow-square-out-active.png";

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: red;
      padding: ${theme.spacing(0, 2.5)};
      line-height: 1.4;
    }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {

      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }
    
        .MuiButton-root {
          display: flex;
          color: #4E4E4E;
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(["color"])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
              fill: #4E4E4E;
            }
          }

          .MuiButton-startIcon {
            color: #4E4E4E;
            fill: #4E4E4E;            
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }
          
          .MuiButton-endIcon {
            color: #4E4E4E;
             fill: #4E4E4E;               
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: #0C76D6;            
            color: #FFFFFF;

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
              fill: #FFFFFF;
            }

            
            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: #FFFFFF;
              fill: #FFFFFF;
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.colors.alpha.trueWhite[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
                  "transform",
                  "opacity",
                ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

const SidebarMenu = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentPath = location.pathname;
  
  const user = useRecoilValue(USER_INFO);
  const { closeSidebar } = useContext(SidebarContext);
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [pencilLineImageSrc, setPencilLineImageSrc] = useState(pencil_line);
  const [magnifyingGlassImageSrc, setMagnifyingGlassImageSrc] = useState(magnifying_glass);
  const [datyImageSrc, setDatyImageSrc] = useState(datyImg);
  const [datyImageLinkSrc, setDatyImageLinkSrc] = useState(arrowSquareOutImg);
  const [datamenityImageSrc, setDatamenityImageSrc] = useState(datamenityImg);
  const [datamenityImageLinkSrc, setDatamenityImageLinkSrc] = useState(arrowSquareOutImg);
  const [easyOrderImageSrc, setEasyOrderImageSrc] = useState(easyOrderImg);
  const [easyOrderImageLinkSrc, setEasyOrderImageLinkSrc] = useState(arrowSquareOutImg);
  // const [openSubMenuVS, setOpenSubMenuVS] = useState(false);
  const [enabledServices, setEnabledServices] = useState({
    chatbot: false,
    price: false
  });

  useEffect(() => {
    if (!user.hotelId) return
    const getService = async () => {
      try {
        const response = await HotelApi.getService({
          hotelId: user.hotelId,
          platform: "revie",
        });
        if (response?.data?.data) {
          setEnabledServices(response.data.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch service:', error);
      }
    }
    getService();
  }, [user.hotelId]);

  const handleSubMenuToggle = () => {
    setOpenSubMenu(!openSubMenu);
  };

  //   const handleSubMenuToggleVS = () => {
  //     setOpenSubMenuVS(!openSubMenuVS);
  //   };

  const nowDate = moment();

  const handleReviewResponseMouseEnter = () => {
    setPencilLineImageSrc(pencil_line_white);
  };
  const handleReviewResponseMouseLeave = () => {
    setPencilLineImageSrc(pencil_line);
  };
  const handleOtaAnalysisMouseEnter = () => {
    setMagnifyingGlassImageSrc(magnifying_glass_white);
  };
  const handleOtaAnalysisMouseLeave = () => {
    setMagnifyingGlassImageSrc(magnifying_glass);
  };
  const handleDatyMouseEnter = () => {
    setDatyImageSrc(datyActiveImg);
    setDatyImageLinkSrc(arrowSquareOutActiveImg);
  };
  const handleDatyMouseLeave = () => {
    setDatyImageSrc(datyImg);
    setDatyImageLinkSrc(arrowSquareOutImg);
  };
  const handleDatamenityMouseEnter = () => {
    setDatamenityImageSrc(datamenityActiveImg);
    setDatamenityImageLinkSrc(arrowSquareOutActiveImg);
  };
  const handleDatamenityMouseLeave = () => {
    setDatamenityImageSrc(datamenityImg);
    setDatamenityImageLinkSrc(arrowSquareOutImg);
  };
  const handleEasyOrderMouseEnter = () => {
    setEasyOrderImageSrc(easyOrderActiveImg);
    setEasyOrderImageLinkSrc(arrowSquareOutActiveImg);
  };
  const handleEasyOrderMouseLeave = () => {
    setEasyOrderImageSrc(easyOrderImg);
    setEasyOrderImageLinkSrc(arrowSquareOutImg);
  };


//   const nowDate = moment("2024-10-28");
//   console.log(nowDate.isBetween("2024-10-27", "2024-11-01"));
//  console.log(!(user?.email=='help@heroworks.co.kr' && (nowDate.isBetween("2024-10-27", "2024-11-01"))));
  return (
    <MenuWrapper>
      <List component="div" style={{ padding: "0 30px" }}>
        <SubMenuWrapper>
          <List component="div" style={{ padding: 0 }}>          
          {!(user?.email=='help@heroworks.co.kr' && (nowDate.isBetween("2024-10-27", "2024-11-01"))) &&
            <ListItem component="div">
              <Button
                component={RouterLink}
                onClick={closeSidebar}
                onMouseEnter={handleReviewResponseMouseEnter}
                onMouseLeave={handleReviewResponseMouseLeave}
                to="/"
                startIcon={
                  currentPath === "/" ? (
                    <img src={pencil_line_white} alt="pencil-line-active" style={{transform: 'scale(0.5)'}} />
                  ) : (
                    <img src={pencilLineImageSrc} alt="pencil-line" style={{transform: 'scale(0.5)'}} />
                  )
                }
                style={{
                  padding: "15px",
                }}
              >
                {t("sidebar_menu.write_review")}
              </Button>
            </ListItem>
        }
            <ListItem component="div">
              <Button
                disableRipple
                onClick={handleSubMenuToggle}
                onMouseEnter={handleOtaAnalysisMouseEnter}
                onMouseLeave={handleOtaAnalysisMouseLeave}
                startIcon={
                  currentPath === "/detailed-analysis" ||
                  currentPath === "/period-analysis" ? (
                    <img
                      src={magnifyingGlassImageSrc}
                      alt="magnifying-glass-active"
                      style={{transform: 'scale(0.5)'}}
                    />
                  ) : (
                    <img src={magnifyingGlassImageSrc} alt="magnifying-glass" style={{transform: 'scale(0.5)'}} />
                  )
                }
                endIcon={
                  openSubMenu ? <ExpandMoreIcon /> : <ChevronRightIcon />
                }
                style={{
                  padding: "15px",
                }}
              >
                {t("sidebar_menu.ota_review_analysis")}
              </Button>
            </ListItem>
            <Collapse in={openSubMenu} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/detailed-analysis"
                  >
                    {t("sidebar_menu.detailed_analysis")}
                  </Button>
                </ListItem>
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/period-analysis"
                  >
                    {t("sidebar_menu.analysis_by_period")}
                  </Button>
                </ListItem>
              </List>
            </Collapse>
            {/* <ListItem component="div">
              <Button
                disableRipple
                onClick={handleSubMenuToggleVS}
                startIcon={
                  currentPath === "/detailed-analysis-by-period" ||
                  currentPath === "/analysis-by-period" ? (
                    <img src={map_pin_fill} alt="map-pin-fill" />
                  ) : (
                    <img src={map_pin} alt="map-pin" />
                  )
                }
                endIcon={
                  openSubMenuVS ? <ExpandMoreIcon /> : <ChevronRightIcon />
                }
                style={{
                  padding: "15px",
                }}
              >
                {t("sidebar_menu.vs_market")}
              </Button>
            </ListItem>
            <Collapse in={openSubMenuVS} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/detailed-analysis-by-period"
                  >
                    {t("sidebar_menu.detailed_analysis")}
                  </Button>
                </ListItem>
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/analysis-by-period"
                  >
                    {t("sidebar_menu.analysis_by_period")}
                  </Button>
                </ListItem>
              </List>
            </Collapse> */}
            {enabledServices.chatbot && (
              <ListItem component="div">
                <Button
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="https://chat.rakuder.co.jp"
                  onMouseEnter={handleDatyMouseEnter}
                  onMouseLeave={handleDatyMouseLeave}
                  // startIcon={<img src={datyImageSrc} alt="chat" />}
                  endIcon={<img src={datyImageLinkSrc} style={{transform: 'scale(0.5)'}} alt="chat_link" />}
                  style={{
                    padding: "15px",
                    paddingLeft: "38px",
                  }}
                >
                  {t("sidebar_menu.easy_chat")}
                </Button>
              </ListItem>
            )}
            {enabledServices.price && (
              <ListItem component="div">
                <Button
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="https://price.rakuder.co.jp"
                  onMouseEnter={handleDatamenityMouseEnter}
                  onMouseLeave={handleDatamenityMouseLeave}
                  // startIcon={<img src={datamenityImageSrc} alt="datamenity" />}
                  endIcon={<img src={datamenityImageLinkSrc} style={{transform: 'scale(0.5)'}} alt="datamenity_link" />}
                  style={{
                    padding: "15px",
                    paddingLeft: "38px",
                  }}
                >
                  {t("sidebar_menu.easy_price")}
                </Button>
              </ListItem>
            )}
          </List>
        </SubMenuWrapper>
      </List>
    </MenuWrapper>
  );
};

export default SidebarMenu;
